import React, { useState } from "react";
import { PageContent } from "@/components/shared/page-content";
import { HeaderSection } from "@/components/shared/header-section";
import { useQuery } from "@apollo/client";
import { GET_ORDERS, DELETE_MANY_ORDERS } from "./OrderQueries";
import { useDeleteConfirmation } from "@/hooks/useDeleteConfirmation";
import { DataTableManual } from "@/components/shared/data-table-manual/data-table-manual";
import { columns } from "./components/columns";
import { DataTableToolbar } from "@/components/shared/data-table-manual/data-table-toolbar-manual";
import { SortingState } from "@tanstack/react-table";
import { orderStatuses } from "@/utils/orderUtils";

export default function Orders() {
  const [pageIndex, setPageIndex] = useState(0);
  const [pageSize, setPageSize] = useState(20);

  const [sorting, setSorting] = useState<SortingState>([
    {
      id: "createdAt",
      desc: true,
    },
  ]);
  const [searchTerm, setSearchTerm] = useState("");
  const [filters, setFilters] = useState<Map<string, Set<string>>>(new Map());

  // Add delete confirmation hook
  const { onDelete } = useDeleteConfirmation({
    mutation: DELETE_MANY_ORDERS,
    refetchQueries: ["GET_ORDERS"],
    messages: {
      success: "Orders deleted successfully",
      error: "Failed to delete orders",
    },
    itemName: "order",
  });

  console.log("sorting", sorting);
  // Prepare query variables
  const queryVariables = React.useMemo(() => {
    const filterArray = Array.from(filters.entries())
      .map(([key, value]) => {
        if (!value.size) return null;
        return {
          [key]: {
            in: Array.from(value),
          },
        };
      })
      .filter(Boolean);

    return {
      take: pageSize,
      skip: pageIndex * pageSize,
      searchTerm: searchTerm || "",
      searchTermInteger: searchTerm ? parseInt(searchTerm) : undefined,
      orderBy: sorting.map((sort) => ({
        [sort.id]: sort.desc ? "desc" : "asc",
      })),
      filters: filterArray,
    };
  }, [pageSize, pageIndex, searchTerm, sorting, filters]);

  const { data, loading } = useQuery(GET_ORDERS, {
    variables: queryVariables,
    fetchPolicy: "network-only", // Always fetch from network when variables change
  });

  // Reset pagination when filters or search changes
  React.useEffect(() => {
    setPageIndex(0);
  }, [searchTerm, filters]);

  const handlePaginationChange = (updater: any) => {
    const newPagination = updater({
      pageIndex,
      pageSize,
    });
    setPageIndex(newPagination.pageIndex);
    setPageSize(newPagination.pageSize);
  };

  const toolbarConfig = {
    onDelete: onDelete,
    search: {
      onSearch: (search: string) => setSearchTerm(search),
      onFilter: (value: any) => setFilters(value),
      placeholder: "Search orders...",
      accessor: "searchTerm",
    },
    columns: [
      {
        title: "Status",
        accessor: "order_status",
        options: Object.entries(orderStatuses).map(([value, label]) => ({
          label,
          value,
        })),
      },
    ],
  };

  return (
    <PageContent>
      <HeaderSection title="Orders" subtitle="Manage and view all orders" />
      <div className="mt-8">
        <DataTableManual
          data={data?.findManyOrder || []}
          columns={columns}
          pageIndex={pageIndex}
          pageSize={pageSize}
          rowCount={data?.findManyOrderCount || 0}
          onPaginationChange={handlePaginationChange}
          onSortingChange={setSorting}
          onColumnFiltersChange={setFilters}
          isLoading={loading}
          toolbar={(table) => (
            <DataTableToolbar table={table} config={toolbarConfig} />
          )}
          defaultSorting={[
            {
              id: "createdAt",
              desc: true,
            },
          ]}
        />
      </div>
    </PageContent>
  );
}
